import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../../design-system';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

/**
 * A route component that checks if the user is authenticated.
 * If not, redirects to the homepage for login/signup.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size={48} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated after loading is complete, redirect to homepage.
  // Stash the intended URL (path + search) so the login flow can return here.
  if (!isAuthenticated) {
    sessionStorage.setItem('redirect_after_auth', location.pathname + location.search);
    return <Navigate to="/" replace />;
  }

  // If authenticated, render the protected content
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
